package fr.manyverse.app.presentation.components

import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.SuspendingPointerInputModifierNode
import androidx.compose.ui.node.CompositionLocalConsumerModifierNode
import androidx.compose.ui.node.DelegatingNode
import androidx.compose.ui.node.ModifierNodeElement
import androidx.compose.ui.node.currentValueOf
import androidx.compose.ui.platform.LocalFocusManager

/**
 * Retire le focus — et replie donc le clavier — quand l'utilisateur tape une zone non interactive.
 *
 * Indispensable sur iOS : le clavier n'y a ni touche de repli ni bouton retour système, l'utilisateur
 * resterait bloqué dans le champ. Les taps consommés par un enfant (bouton, champ texte) ne
 * remontent pas jusqu'ici, seuls les taps « dans le vide » déclenchent le repli.
 *
 * Les dialogues et bottom sheets sont composés dans une couche séparée, avec leur propre
 * `FocusManager` : celui appliqué à la racine de l'app ne les couvre pas. Tout dialogue contenant
 * un champ texte doit donc appliquer ce modificateur à son propre contenu.
 */
fun Modifier.clearFocusOnTap(): Modifier = this then ClearFocusOnTapElement

private data object ClearFocusOnTapElement : ModifierNodeElement<ClearFocusOnTapNode>() {
    override fun create() = ClearFocusOnTapNode()
    override fun update(node: ClearFocusOnTapNode) = Unit
}

// Le FocusManager est lu là où le nœud est attaché (et non là où le modificateur est construit) :
// passé en `modifier` d'un AlertDialog, il cible bien le focus du dialogue et non celui de l'écran.
private class ClearFocusOnTapNode : DelegatingNode(), CompositionLocalConsumerModifierNode {
    init {
        delegate(SuspendingPointerInputModifierNode {
            detectTapGestures(onTap = { currentValueOf(LocalFocusManager).clearFocus() })
        })
    }
}
